import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from 'carbon-react/lib/components/button';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import {
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { ReportSchedulingView } from '@futrli/views/ReportSchedulingView';
import { MembersView } from '@futrli/views/MembersView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { useRouteIds } from '../../../hooks/useRouteIds';
import { useScheduleReportForm } from './hooks/useScheduleReportForm';
import { ScheduleFormScreen } from './screens/schedule-form-screen';

export interface CreateScheduleContainerProps {
  onClose: () => void;
  reportId: string;
  reportName: string;
}

type Screen = 'form' | 'loading' | 'success';

export const CreateScheduleContainer = ({
  onClose,
  reportId,
  reportName
}: CreateScheduleContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(
      () =>
        new ReportSchedulingView(workspaceId, organisationId, isConsolidation, {
          schedules: false,
          awaitingReview: false,
          dispatched: false,
          exported: false
        }),
      [workspaceId, organisationId, isConsolidation]
    )
  );

  const membersView = useApiView(
    useMemo(() => new MembersView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const form = useScheduleReportForm({
    reportId,
    reportName,
    members: membersView.state.members
  });

  useEffect(() => {
    if (view.state.isCreatingSchedule) {
      setScreen('loading');
    }
  }, [view.state.isCreatingSchedule]);

  useEffect(() => {
    if (screen === 'loading' && !view.state.isCreatingSchedule && !view.state.errorCreatingSchedule) {
      setScreen('success');
    }
  }, [screen, view.state.isCreatingSchedule, view.state.errorCreatingSchedule]);

  useEffect(() => {
    if (view.state.errorCreatingSchedule) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorCreatingSchedule, intl]);

  const handleSubmit = useCallback(() => {
    const payload = form.buildPayload();
    if (!payload) {
      return;
    }

    setError(undefined);
    view.createSchedule({ data: payload });
  }, [form, view]);

  if (membersView.state.isLoading) {
    return <LoadingWizardScreen />;
  }

  if (screen === 'loading') {
    return (
      <LoadingWizardScreen
        text={intl.formatMessage({ id: 'predict.schedule_creating', defaultMessage: 'Creating report schedule...' })}
      />
    );
  }

  if (screen === 'success') {
    return (
      <WizardScreen
        rightSideButtons={
          <Button buttonType="primary" onClick={onClose}>
            {intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}
          </Button>
        }
      >
        <WizardFinished
          text={intl.formatMessage({ id: 'predict.schedule_report_scheduled', defaultMessage: 'Report scheduled' })}
        />
      </WizardScreen>
    );
  }

  return (
    <ScheduleFormScreen
      reportName={reportName}
      members={membersView.state.members}
      form={form}
      isEditing={false}
      error={error}
      onCancel={onClose}
      onSubmit={handleSubmit}
    />
  );
};
